import { FormEvent, useCallback, useEffect, useState } from 'react';
import { dealerErrorMessage, dealersApi, isFeatureOff } from '../../services/dealersApi';
import { Dealer, DealerAction, DealerDocument, DealerDocumentType, DealerUser, Showroom } from '../../types/dealers';
import { EMIRATES } from '../../types/delivery';
import DealerStatusChip from './DealerStatusChip';
import DealerUserForm from './DealerUserForm';
import ShowroomForm from './ShowroomForm';
import { aed, formatDate, formatDateTime, humanize, todayDubai } from './dealerFormat';

/**
 * Spec 085 §4 — the smaller tabs of DealerDetailPage (Users, Showrooms, Documents, Activity) and the
 * agreement panel on Profile. Each loads on its own and refetches after its own mutations.
 */

interface TabProps {
  dealerId: string;
  token: string;
  notify: (message: string, type?: 'success' | 'error') => void;
  onFeatureOff: () => void;
}

function useList<T>(load: () => Promise<T[]>) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await load());
    } catch (err) {
      setError(dealerErrorMessage(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [load]);
  useEffect(() => { reload(); }, [reload]);
  return { rows, loading, error, reload };
}

function ListState({ loading, error, empty, emptyText, onRetry }: {
  loading: boolean; error: string | null; empty: boolean; emptyText: string; onRetry: () => void;
}) {
  if (loading) return <div className="admin-loading"><div className="admin-loading-spinner" /></div>;
  if (error) {
    return (
      <div>
        <div className="admin-info-banner danger" role="alert">{error}</div>
        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (empty) return <div className="admin-empty-state"><p>{emptyText}</p></div>;
  return null;
}

// ── Users ──

export function UsersTab({ dealerId, token, notify, onFeatureOff }: TabProps) {
  const load = useCallback(() => dealersApi.users(dealerId, token), [dealerId, token]);
  const { rows, loading, error, reload } = useList<DealerUser>(load);
  const [editing, setEditing] = useState<DealerUser | null | 'new'>(null);

  return (
    <div data-testid="dealer-users">
      <div className="admin-toolbar dealer-toolbar">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Dealer users</h3>
        <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setEditing('new')}>Add user</button>
      </div>
      <ListState loading={loading} error={error} empty={rows.length === 0} emptyText="No users yet. Add the dealer owner first." onRetry={reload} />
      {!loading && !error && rows.length > 0 && (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead>
              <tr><th>Name</th><th>Username</th><th>Phone</th><th>Email</th><th>Role</th><th>Can commit spend</th><th>Status</th><th>Agreement accepted</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td dir="ltr">{u.username ?? <span className="dealer-muted" title="Created before usernames: signs in with the email">email sign-in</span>}</td>
                  <td>{u.phone}</td>
                  <td>{u.email}</td>
                  <td>{humanize(u.role)}</td>
                  <td>{u.canCommitSpend ? 'Yes' : 'No'}</td>
                  <td><DealerStatusChip status={u.status} testId="user-status" /></td>
                  <td>{formatDate(u.agreementAcceptedAt)}</td>
                  <td className="dealer-actions">
                    <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditing(u)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <DealerUserForm
          dealerId={dealerId}
          user={editing === 'new' ? null : editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            notify(editing === 'new' ? `${saved.name} added; the welcome message is on its way` : `${saved.name} saved`);
            setEditing(null);
            reload();
          }}
          onFeatureOff={onFeatureOff}
        />
      )}
    </div>
  );
}

// ── Showrooms ──

export function ShowroomsTab({ dealerId, token, notify, onFeatureOff }: TabProps) {
  const load = useCallback(() => dealersApi.showrooms(dealerId, token), [dealerId, token]);
  const { rows, loading, error, reload } = useList<Showroom>(load);
  const [editing, setEditing] = useState<Showroom | null | 'new'>(null);

  const deactivate = async (s: Showroom) => {
    if (!window.confirm(`Deactivate ${s.label}? Buyers can no longer pick it for pickup or drop-off.`)) return;
    try {
      await dealersApi.deleteShowroom(dealerId, s.id, token);
      notify(`${s.label} deactivated`);
      reload();
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); return; }
      notify(dealerErrorMessage(err, 'Failed to deactivate'), 'error');
    }
  };

  const address = (s: Showroom) => [s.building, s.street, s.area, s.city, s.emirate ? EMIRATES.find((em) => em.code === s.emirate)?.label ?? s.emirate : null]
    .filter(Boolean).join(', ');

  return (
    <div data-testid="dealer-showrooms">
      <div className="admin-toolbar dealer-toolbar">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Showrooms</h3>
        <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setEditing('new')}>Add showroom</button>
      </div>
      <ListState loading={loading} error={error} empty={rows.length === 0} emptyText="No showrooms yet." onRetry={reload} />
      {!loading && !error && rows.length > 0 && (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead><tr><th>Label</th><th>Address</th><th>Map pin</th><th>Status</th><th aria-label="Actions" /></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 600 }}>{s.label}</td>
                  <td>{address(s) || '—'}</td>
                  <td>{s.latitude != null && s.longitude != null ? `${Number(s.latitude).toFixed(5)}, ${Number(s.longitude).toFixed(5)}` : '—'}</td>
                  <td><DealerStatusChip status={s.status} testId="showroom-status" /></td>
                  <td className="dealer-actions">
                    {s.status === 'ACTIVE' && (
                      <>
                        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditing(s)}>Edit</button>
                        <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => deactivate(s)}>Deactivate</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <ShowroomForm
          dealerId={dealerId}
          showroom={editing === 'new' ? null : editing}
          token={token}
          onClose={() => setEditing(null)}
          onSaved={(saved) => { notify(`${saved.label} saved`); setEditing(null); reload(); }}
          onFeatureOff={onFeatureOff}
        />
      )}
    </div>
  );
}

// ── Documents ──

const DOC_TYPES: { value: DealerDocumentType | ''; label: string }[] = [
  { value: '', label: 'All documents' },
  { value: 'PROFORMA', label: 'Proformas (DPF)' },
  { value: 'TAX_INVOICE', label: 'Tax invoices (DINV)' },
  { value: 'CREDIT_NOTE', label: 'Credit notes (DCN)' },
];

export function DocumentsTab({ dealerId, token, refreshKey = 0 }: Omit<TabProps, 'notify' | 'onFeatureOff'> & { refreshKey?: number }) {
  const [type, setType] = useState<DealerDocumentType | ''>('');
  const load = useCallback(() => dealersApi.documents(dealerId, token, type || undefined), [dealerId, token, type, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const { rows, loading, error, reload } = useList<DealerDocument>(load);

  return (
    <div data-testid="dealer-documents">
      <div className="admin-toolbar dealer-toolbar">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Documents</h3>
        <select className="admin-select" aria-label="Filter documents by type" value={type} onChange={(e) => setType(e.target.value as DealerDocumentType | '')}>
          {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <ListState loading={loading} error={error} empty={rows.length === 0} emptyText="No documents yet." onRetry={reload} />
      {!loading && !error && rows.length > 0 && (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead><tr><th>Number</th><th>Type</th><th className="num">Amount</th><th className="num">VAT</th><th>Status</th><th>Created</th><th aria-label="Download" /></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td>{d.number ?? '—'}</td>
                  <td>{humanize(d.type)}</td>
                  <td className="num">{aed(d.amountAed)}</td>
                  <td className="num">{aed(d.vatAed)}</td>
                  <td><DealerStatusChip status={d.status} testId="document-status" /></td>
                  <td>{formatDate(d.createdAt)}</td>
                  <td className="dealer-actions">
                    {d.downloadUrl
                      ? <a className="admin-btn admin-btn-outline admin-btn-sm" href={d.downloadUrl} target="_blank" rel="noreferrer">Download</a>
                      : <span className="dealer-cell-note">Not ready</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Activity ──

export function ActivityTab({ dealerId, token }: Omit<TabProps, 'notify' | 'onFeatureOff'>) {
  const load = useCallback(() => dealersApi.audit(dealerId, token), [dealerId, token]);
  const { rows, loading, error, reload } = useList<DealerAction>(load);
  return (
    <div data-testid="dealer-activity">
      <h3 className="admin-card-title dealer-section-title">Dealer user activity</h3>
      <ListState loading={loading} error={error} empty={rows.length === 0} emptyText="No dealer user actions yet." onRetry={reload} />
      {!loading && !error && rows.length > 0 && (
        <div className="admin-table-wrapper">
          <table className="admin-table dealer-table">
            <thead><tr><th>When</th><th>Action</th><th>Target</th><th>Details</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>{formatDateTime(a.createdAt)}</td>
                  <td>{humanize(a.action)}</td>
                  <td>{a.targetType ? humanize(a.targetType) : '—'}</td>
                  <td className="dealer-cell-note">
                    {a.details ? Object.entries(a.details).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`).join(' · ') : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="dealer-muted">Admin changes to this dealer are in Audit.</div>
    </div>
  );
}

// ── Agreement (Profile) ──

const MAX_PDF_BYTES = 10 * 1024 * 1024;

/** BR-4 — the signed agreement PDF + signed date; activation needs it on file. */
export function AgreementPanel({ dealer, token, notify, onFeatureOff, onChanged }: {
  dealer: Dealer;
  token: string;
  notify: (message: string, type?: 'success' | 'error') => void;
  onFeatureOff: () => void;
  onChanged: (dealer?: Dealer) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [signedAt, setSignedAt] = useState(todayDubai());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!file) { setError('Choose the signed PDF'); return; }
    if (file.type && file.type !== 'application/pdf') { setError('The agreement must be a PDF'); return; }
    if (file.size > MAX_PDF_BYTES) { setError('The PDF must be 10 MB or smaller'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(signedAt)) { setError('Pick the signed date'); return; }
    setBusy(true);
    try {
      const saved = await dealersApi.uploadAgreement(dealer.id, file, signedAt, token);
      setFile(null);
      notify('Agreement uploaded');
      onChanged(saved);
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); return; }
      setError(dealerErrorMessage(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the agreement on file? An inactive dealer cannot be activated without one.')) return;
    setBusy(true);
    try {
      await dealersApi.deleteAgreement(dealer.id, token);
      notify('Agreement removed');
      onChanged();
    } catch (err) {
      if (isFeatureOff(err)) { onFeatureOff(); return; }
      notify(dealerErrorMessage(err, 'Failed to remove the agreement'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-card" data-testid="dealer-agreement">
      <h3 className="admin-card-title">Signed agreement</h3>
      {dealer.agreementOnFile ? (
        <div className="dealer-agreement-row">
          <span className="admin-badge dealer-badge approved">On file</span>
          <span>Signed {formatDate(dealer.agreementSignedAt)}</span>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={busy || dealer.status === 'ACTIVE'}
            title={dealer.status === 'ACTIVE' ? 'Suspend the dealer before removing its agreement' : undefined} onClick={remove}>Remove</button>
          {dealer.status === 'ACTIVE' && <span className="dealer-muted">Suspend the dealer to remove it.</span>}
        </div>
      ) : (
        <div className="admin-info-banner warning" role="note">No agreement on file. Upload the signed PDF before activating the dealer.</div>
      )}
      <form className="dealer-agreement-form" onSubmit={upload} noValidate>
        <label className="admin-form-label" htmlFor="agreement-file">{dealer.agreementOnFile ? 'Replace with a new signed PDF' : 'Signed PDF'}</label>
        <input id="agreement-file" type="file" accept="application/pdf" disabled={busy} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <label className="admin-form-label" htmlFor="agreement-signed-at">Signed on</label>
        <input id="agreement-signed-at" className="admin-form-input" type="date" value={signedAt} disabled={busy} style={{ maxWidth: '12rem' }}
          onChange={(e) => setSignedAt(e.target.value)} />
        <button type="submit" className="admin-btn admin-btn-primary admin-btn-sm" disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button>
      </form>
      {error && <div className="admin-info-banner danger" role="alert" data-testid="agreement-error">{error}</div>}
    </div>
  );
}
